import React, { useState, useEffect } from 'react';
import * as supabaseService from './services/supabaseService';
import './AdminPanel.css';

const AdminPanel = ({ currentUser, onClose }) => {
  // Verificação de admin - apenas herick.8434158@edu.univali.br pode acessar
  if (currentUser?.email !== 'herick.8434158@edu.univali.br') {
    onClose();
    return null;
  }

  const [activeTab, setActiveTab] = useState('universities');
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  
  // Form states para adicionar universidade
  const [newUniversity, setNewUniversity] = useState({
    nome: '',
    sigla: '',
    dominio: '',
    logo_url: '',
    cor_tag: '#667eea',
    ativa: true
  });

  useEffect(() => {
    loadUniversities();
  }, []);

  const loadUniversities = async () => {
    setLoading(true);
    try {
      const data = await supabaseService.getAllUniversities();
      setUniversities(data || []);
    } catch (error) {
      showToast('Erro ao carregar universidades', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUniversity = async () => {
    if (!newUniversity.nome || !newUniversity.sigla || !newUniversity.dominio) {
      showToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    setLoading(true);
    try {
      await supabaseService.addUniversity(newUniversity);
      showToast('Universidade adicionada com sucesso!', 'success');
      setShowAddModal(false);
      setNewUniversity({
        nome: '',
        sigla: '',
        dominio: '',
        logo_url: '',
        ativa: true
      });
      loadUniversities();
    } catch (error) {
      showToast('Erro ao adicionar universidade', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUniversity = (university) => {
    setEditingUniversity(university);
    setNewUniversity({
      nome: university.nome,
      sigla: university.sigla,
      dominio: university.dominio,
      logo_url: university.logo_url || '',
      cor_tag: university.cor_tag || '#667eea',
      ativa: university.ativa
    });
    setShowEditModal(true);
  };

  const handleUpdateUniversity = async () => {
    if (!newUniversity.nome || !newUniversity.sigla || !newUniversity.dominio) {
      showToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    setLoading(true);
    try {
      await supabaseService.updateUniversity(editingUniversity.id, newUniversity);
      showToast('Universidade atualizada com sucesso!', 'success');
      setShowEditModal(false);
      setEditingUniversity(null);
      setNewUniversity({
        nome: '',
        sigla: '',
        dominio: '',
        logo_url: '',
        cor_tag: '#667eea',
        ativa: true
      });
      loadUniversities();
    } catch (error) {
      showToast('Erro ao atualizar universidade', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUniversity = async (id, currentStatus) => {
    try {
      await supabaseService.toggleUniversityStatus(id, !currentStatus);
      showToast(`Universidade ${!currentStatus ? 'ativada' : 'desativada'} com sucesso!`, 'success');
      loadUniversities();
    } catch (error) {
      showToast('Erro ao atualizar status', 'error');
    }
  };

  const handleDeleteUniversity = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta universidade?')) {
      return;
    }

    try {
      await supabaseService.deleteUniversity(id);
      showToast('Universidade excluída com sucesso!', 'success');
      loadUniversities();
    } catch (error) {
      showToast('Erro ao excluir universidade', 'error');
    }
  };

  const showToast = (message, type = 'info') => {
    // Toast simples
    const toast = document.createElement('div');
    toast.className = `admin-toast admin-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  };

  return (
    <div className="admin-panel-overlay">
      <div className="admin-panel">
        <div className="admin-panel-header">
          <h2>🔧 Painel Administrativo</h2>
          <button className="admin-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'universities' ? 'active' : ''}`}
            onClick={() => setActiveTab('universities')}
          >
            🎓 Universidades
          </button>
          <button 
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 Usuários
          </button>
          <button 
            className={`admin-tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📊 Estatísticas
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'universities' && (
            <div className="admin-universities">
              <div className="admin-section-header">
                <h3>Gerenciar Universidades</h3>
                <button 
                  className="admin-add-btn"
                  onClick={() => setShowAddModal(true)}
                >
                  ➕ Adicionar Universidade
                </button>
              </div>

              {loading ? (
                <div className="admin-loading">Carregando...</div>
              ) : (
                <div className="admin-universities-list">
                  {universities.length === 0 ? (
                    <div className="admin-empty">
                      Nenhuma universidade cadastrada. Adicione a primeira!
                    </div>
                  ) : (
                    universities.map(uni => (
                      <div key={uni.id} className="admin-university-card">
                        <div className="admin-university-info">
                          {uni.logo_url && (
                            <img 
                              src={uni.logo_url} 
                              alt={uni.nome}
                              className="admin-university-logo"
                            />
                          )}
                          <div className="admin-university-details">
                            <h4>{uni.nome}</h4>
                            <p className="admin-university-sigla">{uni.sigla}</p>
                            <p className="admin-university-domain">
                              📧 Domínio: <code>{uni.dominio}</code>
                            </p>
                            {uni.cor_tag && (
                              <p className="admin-university-color">
                                🎨 Cor: <span 
                                  style={{
                                    display: 'inline-block',
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: uni.cor_tag,
                                    borderRadius: '4px',
                                    verticalAlign: 'middle',
                                    marginLeft: '4px',
                                    border: '1px solid #ddd'
                                  }}
                                ></span>
                              </p>
                            )}
                            <span className={`admin-status-badge ${uni.ativa ? 'active' : 'inactive'}`}>
                              {uni.ativa ? '✓ Ativa' : '✗ Inativa'}
                            </span>
                          </div>
                        </div>
                        <div className="admin-university-actions">
                          <button
                            className="admin-action-btn edit"
                            onClick={() => handleEditUniversity(uni)}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            className={`admin-action-btn ${uni.ativa ? 'deactivate' : 'activate'}`}
                            onClick={() => handleToggleUniversity(uni.id, uni.ativa)}
                          >
                            {uni.ativa ? '🔴 Desativar' : '🟢 Ativar'}
                          </button>
                          <button
                            className="admin-action-btn delete"
                            onClick={() => handleDeleteUniversity(uni.id)}
                          >
                            🗑️ Excluir
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="admin-users">
              <h3>Gerenciar Usuários</h3>
              <p>Em desenvolvimento...</p>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="admin-stats">
              <h3>Estatísticas do Sistema</h3>
              <p>Em desenvolvimento...</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal para adicionar universidade */}
      {showAddModal && (
        <div className="admin-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>➕ Adicionar Universidade</h3>
              <button className="admin-close-btn" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            
            <div className="admin-modal-content">
              <div className="admin-form-group">
                <label>Nome da Universidade *</label>
                <input
                  type="text"
                  placeholder="Ex: Universidade do Vale do Itajaí"
                  value={newUniversity.nome}
                  onChange={(e) => setNewUniversity({...newUniversity, nome: e.target.value})}
                />
              </div>

              <div className="admin-form-group">
                <label>Sigla *</label>
                <input
                  type="text"
                  placeholder="Ex: UNIVALI"
                  value={newUniversity.sigla}
                  onChange={(e) => setNewUniversity({...newUniversity, sigla: e.target.value.toUpperCase()})}
                />
              </div>

              <div className="admin-form-group">
                <label>Domínio de E-mail *</label>
                <input
                  type="text"
                  placeholder="Ex: edu.univali.br"
                  value={newUniversity.dominio}
                  onChange={(e) => setNewUniversity({...newUniversity, dominio: e.target.value.toLowerCase()})}
                />
                <small>Apenas e-mails com este domínio poderão se cadastrar</small>
              </div>

              <div className="admin-form-group">
                <label>URL do Logo (opcional)</label>
                <input
                  type="text"
                  placeholder="https://exemplo.com/logo.png"
                  value={newUniversity.logo_url}
                  onChange={(e) => setNewUniversity({...newUniversity, logo_url: e.target.value})}
                />
              </div>

              <div className="admin-form-group">
                <label>Cor da Tag</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={newUniversity.cor_tag}
                    onChange={(e) => setNewUniversity({...newUniversity, cor_tag: e.target.value})}
                    style={{ width: '60px', height: '40px', cursor: 'pointer', border: '2px solid #e5eaf5', borderRadius: '6px' }}
                  />
                  <input
                    type="text"
                    value={newUniversity.cor_tag}
                    onChange={(e) => setNewUniversity({...newUniversity, cor_tag: e.target.value})}
                    placeholder="#667eea"
                    style={{ flex: 1 }}
                  />
                  <div style={{ 
                    padding: '8px 16px', 
                    backgroundColor: newUniversity.cor_tag, 
                    color: 'white',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '0.9em'
                  }}>
                    Preview
                  </div>
                </div>
                <small>Cor da tag que aparecerá no perfil do usuário</small>
              </div>

              <div className="admin-form-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={newUniversity.ativa}
                    onChange={(e) => setNewUniversity({...newUniversity, ativa: e.target.checked})}
                  />
                  <span>Universidade ativa (permitir cadastros)</span>
                </label>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button 
                className="admin-btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="admin-btn-primary"
                onClick={handleAddUniversity}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar universidade */}
      {showEditModal && (
        <div className="admin-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>✏️ Editar Universidade</h3>
              <button className="admin-close-btn" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            
            <div className="admin-modal-content">
              <div className="admin-form-group">
                <label>Nome da Universidade *</label>
                <input
                  type="text"
                  placeholder="Ex: Universidade do Vale do Itajaí"
                  value={newUniversity.nome}
                  onChange={(e) => setNewUniversity({...newUniversity, nome: e.target.value})}
                />
              </div>

              <div className="admin-form-group">
                <label>Sigla *</label>
                <input
                  type="text"
                  placeholder="Ex: UNIVALI"
                  value={newUniversity.sigla}
                  onChange={(e) => setNewUniversity({...newUniversity, sigla: e.target.value.toUpperCase()})}
                />
              </div>

              <div className="admin-form-group">
                <label>Domínio de E-mail *</label>
                <input
                  type="text"
                  placeholder="Ex: edu.univali.br"
                  value={newUniversity.dominio}
                  onChange={(e) => setNewUniversity({...newUniversity, dominio: e.target.value.toLowerCase()})}
                />
                <small>Apenas e-mails com este domínio poderão se cadastrar</small>
              </div>

              <div className="admin-form-group">
                <label>URL do Logo (opcional)</label>
                <input
                  type="text"
                  placeholder="https://exemplo.com/logo.png"
                  value={newUniversity.logo_url}
                  onChange={(e) => setNewUniversity({...newUniversity, logo_url: e.target.value})}
                />
              </div>

              <div className="admin-form-group">
                <label>Cor da Tag</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={newUniversity.cor_tag}
                    onChange={(e) => setNewUniversity({...newUniversity, cor_tag: e.target.value})}
                    style={{ width: '60px', height: '40px', cursor: 'pointer', border: '2px solid #e5eaf5', borderRadius: '6px' }}
                  />
                  <input
                    type="text"
                    value={newUniversity.cor_tag}
                    onChange={(e) => setNewUniversity({...newUniversity, cor_tag: e.target.value})}
                    placeholder="#667eea"
                    style={{ flex: 1 }}
                  />
                  <div style={{ 
                    padding: '8px 16px', 
                    backgroundColor: newUniversity.cor_tag, 
                    color: 'white',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '0.9em'
                  }}>
                    Preview
                  </div>
                </div>
                <small>Cor da tag que aparecerá no perfil do usuário</small>
              </div>

              <div className="admin-form-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={newUniversity.ativa}
                    onChange={(e) => setNewUniversity({...newUniversity, ativa: e.target.checked})}
                  />
                  <span>Universidade ativa (permitir cadastros)</span>
                </label>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button 
                className="admin-btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="admin-btn-primary"
                onClick={handleUpdateUniversity}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
